import { inject, Injectable } from '@angular/core';

import { StorageAPIService } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { DownloadTasksService, Task, TaskPayload, TaskType } from '../tasks-api/download.service';
import { urlFor } from './utils';

export interface SmartSummaryCategorizedResponse {
  likeText: SmartSummary[];
  dislikeText: SmartSummary[];
  improveText: SmartSummary[];
}

export interface SmartSummaryMultiResponse {
  summaries: SmartSummary[];
}

export interface EmbeddingOptions {
  models: string[];
  strategies: string[];
}

export interface SmartSummary {
  summaryId: string;
  summary: string;
  header?: string;
  breakdownField?: string;
  breakdownValue?: string;
  regenerating?: boolean;
  counts: SummaryCounts;
  countToolTip?: string;
  percentage?: number;
  examples?: VerbatimExample[];
  exampleSectionHeader?: string;
  examplesRequested: boolean;
  ignoredExamples?: VerbatimExample[];
  examplesExhausted: boolean;
  anotherExampleTooltip?: string;
  downloadRequestTooltip?: string;
  currentlyDownloadingSpotERequest?: boolean;
  downloadEmbeddingReportTooltip?: string;
  currentlyDownloadingEmbeddingReport?: boolean;
  accuracyReportTooltip?: string;
  currentlyDownloadingAccuracyReport?: boolean;
  currentlyGeneratingFeedback?: boolean;
  summaryFeedbackTooltip?: string;
  disableRemove?: boolean;
  disableRemoveTooltip?: string;
  isErrored?: boolean;
  isDummy?: boolean;
  elasticRandomSeed?: string;
  spoteRequestId?: string;
  spotESceneInfo?: SpoteSceneInfo;
  feedbackId?: string;
  summaryAnalysis?: SummaryAnalysis;
}

interface SummaryAnalysis {
  status: string;
  errorMessage?: string;
  configId?: string;
  taxonomy: any[];
  entities: any[];
}

export interface SmartSummaryFeedbackReport {
  id: string;
  accountId: string;
  projectId: string;
  dashboardId: string;
  dataSourceId: string;
  collectionId: string;
  widgetId: string;
  feedbackType: 'positive' | 'negative';
  elasticQuery: string;
  documentId: string;
  summaryData: SmartSummary;
  userFeedback: string;
}

export interface SpoteScenarioInfo {
  id: string;
  version: number;
}

export interface SpoteSceneInfo {
  id: string;
  version: string;
  evalScenario: SpoteScenarioInfo;
  mergeScenario: SpoteScenarioInfo;
}

export interface SummaryCounts {
  vectorCount: number;
  totalCount: number;
  countError?: boolean;
}

export interface SpotERequest {
  data: string[];
  parameters: InteractiveSmartSummaryParams;
}

export interface InteractiveSmartSummaryParams {
  analyticItemType: string;
  analyticItemName: string;
  paragraphLength?: number;
  existingSummaries?: string[];
  category?: string;
  breakdownField?: string;
  breakdownValue?: string;
  similarity?: string;
  elasticRandomSeed?: string;
  elasticQuery?: string;
}

export interface SummaryFeedBackParameters {
  feedbackId?: string;
  summary: SmartSummary;
  smartSummaryParameters: InteractiveSmartSummaryParams;
  userFeedback: string;
  feedbackType: string;
  projectId: string;
  widgetId: string;
  dashboardId: string;
  collectionId: string;
  dataSourceId: string;
}

export interface VerbatimExample {
  text: string;
  id: string;
  publicId: string;
  fetchingAnother?: boolean;
}

export interface EmbeddingDocumentDump {
  document?: string;
  documentId?: string;
  score?: string;
  summary?: string;
}

export interface CheckEmbeddingsResult {
  analysisDocumentCount: number;
  embeddingCount: number;
}

export interface AccuracyResults {
  accuracyResults: string;
}

interface InteractiveSmartSummaryTaskPayload extends TaskPayload {
  widgetId: string;
  dashboardId: string;
  projectId: string;
  collectionId: string;
  analysisGroupId?: string;
  analysisId?: string;
  queryString?: string;
  timezone: string;
  analyticItemType: string;
  analyticItemName: string;
}

export type InteractiveSmartSummaryTask = Task<
  InteractiveSmartSummaryTaskPayload,
  InteractiveSmartSummary
>;

export interface InteractiveSmartSummary {
  id: string;
  status: 'RUNNING' | 'COMPLETED' | 'ERROR';
  startedAt: Date;
  finishedAt?: Date;
  widgetId: string;
  dataSourceId: string;
  dashboardId: string;
  projectId: string;
  results?: SmartSummaryHierarchyNode[];
  payload: SmartSummaryWidgetPayload;
  similarity?: number;
}

export interface SmartSummaryHierarchyNode {
  name: string;
  summaries: SmartSummary[];
  children: SmartSummaryHierarchyNode[];
  isTopLevel: boolean;
  category: string;
  isBreakdown: boolean;
  breakdownField?: string;
  rejectedSummaries: SmartSummary[];
}

export interface SmartSummaryGetResponse {
  nextPageToken?: string;
  items: InteractiveSmartSummary[];
  count: number;
}

export interface SmartSummaryWidgetPayload {
  analyticItemType: string;
  analyticItemName: string;
  query?: string;
}

@EnableClassTelemetry()
@Injectable()
export class InteractiveSmartSummaryService extends StorageAPIService {
  private taskService = inject(DownloadTasksService);
  private realtimeService = inject(RealtimeService);

  public on(
    namespace: string,
    fn: RealtimeEventHandler<InteractiveSmartSummary>
  ): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        smartSummary: (data: RealtimeMessage<InteractiveSmartSummary>) => {
          fn(data);
        },
      },
      'interactiveSmartSummaryService'
    );
  }

  createSmartSummaryTask(
    projectId: string,
    collectionId: string,
    analysisGroupId: string,
    analysisId: string,
    widgetId: string,
    dashboardId: string,
    body: InteractiveSmartSummaryParams,
    timezone: string,
    query?: string
  ) {
    const payload: InteractiveSmartSummaryTaskPayload = {
      _type: TaskType.INTERACTIVE_SMART_SUMMARY,
      widgetId,
      dashboardId,
      projectId,
      collectionId,
      analysisGroupId,
      analysisId,
      queryString: query,
      timezone,
      analyticItemType: body.analyticItemType,
      analyticItemName: body.analyticItemName,
    };
    return this.taskService.createTask<InteractiveSmartSummaryTaskPayload, InteractiveSmartSummary>(
      payload
    );
  }

  async getSmartSummaries(
    projectId: string,
    dashboardId: string,
    widgetId?: string
  ): Promise<InteractiveSmartSummary[]> {
    let allItems: InteractiveSmartSummary[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const params: any = { widgetId };
      if (pageToken) {
        params['page-token'] = pageToken;
      }

      const response = await this.get<SmartSummaryGetResponse>(
        `/projects/${projectId}/dashboards/${dashboardId}/smart-summary`,
        params
      );

      allItems = allItems.concat(response.items);
      pageToken = response.nextPageToken;
    } while (pageToken);

    return allItems;
  }

  getSmartSummaryFeedback(
    projectId: string,
    feedbackId: string
  ): Promise<SmartSummaryFeedbackReport> {
    return this.get(`/projects/${projectId}/smart-summary/feedback/${feedbackId}`);
  }

  deleteSmartSummaryFeedback(projectId: string, feedbackId: string): Promise<void> {
    return this.delete<void>(
      urlFor(`/smart-summary/feedback/${encodeURIComponent(feedbackId)}`, projectId)
    );
  }

  getEmbeddingOptions(): Promise<EmbeddingOptions> {
    return this.get(`/smart-summary/embedding-options`);
  }

  public postSmartSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    body: InteractiveSmartSummaryParams,
    timezone: string,
    query?: string,
    projectId?: string
  ): Promise<SmartSummaryCategorizedResponse> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/smart-summary`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/smart-summary`,
        projectId
      );
    }

    return this.post<SmartSummaryCategorizedResponse>(url, body, {}, { query, timezone });
  }

  public regenerateSmartSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    body: InteractiveSmartSummaryParams,
    timezone: string,
    query?: string,
    projectId?: string
  ): Promise<SmartSummary> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/smart-summary/_regenerate`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/smart-summary/_regenerate`,
        projectId
      );
    }
    return this.post<SmartSummary>(url, body, {}, { query, timezone });
  }

  public analyzeSmartSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    smartSummary: SmartSummary,
    timezone: string,
    projectId?: string,
    configId?: string
  ): Promise<SmartSummary> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/smart-summary/_analyze`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/smart-summary/_analyze`,
        projectId
      );
    }
    return this.post<SmartSummary>(url, smartSummary, {}, { configId, timezone });
  }

  public getSpotEReqestForSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    body: InteractiveSmartSummaryParams,
    timezone: string,
    query?: string,
    projectId?: string
  ): Promise<SpotERequest> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/smart-summary/_spote-request`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/smart-summary/_spote-request`,
        projectId
      );
    }
    return this.post<SpotERequest>(url, body, {}, { query, timezone });
  }

  public generateFeedBackForSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    body: SummaryFeedBackParameters,
    timezone: string,
    query?: string,
    projectId?: string
  ): Promise<SmartSummaryFeedbackReport> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/smart-summary/_generate_feedback`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/smart-summary/_generate_feedback`,
        projectId
      );
    }
    return this.post<SmartSummaryFeedbackReport>(url, body, {}, { query, timezone });
  }

  public breakdownSmartSummary(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    body: InteractiveSmartSummaryParams,
    breakdownField: string,
    timezone: string,
    query?: string,
    projectId?: string
  ): Promise<SmartSummaryMultiResponse> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/breakdown-smart-summary`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/breakdown-smart-summary`,
        projectId
      );
    }
    return this.post<SmartSummaryMultiResponse>(url, body, {}, { query, timezone, breakdownField });
  }

  public getSmartSummaryExamples(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    summary: SmartSummary,
    query: string,
    projectId: string,
    similarity: string,
    timezone: string
  ): Promise<VerbatimExample[]> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/summary-examples`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/summary-examples`,
        projectId
      );
    }
    const toIgnore: string[] = [];
    if (summary.examples) {
      summary.examples.forEach((ex) => toIgnore.push(ex.publicId));
    }
    if (summary.ignoredExamples) {
      summary.ignoredExamples.forEach((ex) => toIgnore.push(ex.publicId));
    }
    return this.post<VerbatimExample[]>(
      url,
      {
        summary: summary.summary,
        breakdownField: summary.breakdownField,
        breakdownValue: summary.breakdownValue,
        ignoreIds: toIgnore.length > 0 ? toIgnore : undefined,
        similarity,
      },
      {},
      { query, timezone }
    );
  }

  public getSmartSummaryCounts(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    summary: SmartSummary,
    query: string,
    projectId: string,
    similarity: string,
    timezone: string
  ): Promise<SummaryCounts> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/count-summary-examples`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/count-summary-examples`,
        projectId
      );
    }
    return this.post<SummaryCounts>(
      url,
      {
        summary: summary.summary,
        breakdownField: summary.breakdownField,
        breakdownValue: summary.breakdownValue,
        similarity,
      },
      {},
      { query, timezone }
    );
  }

  public runEmbedding(summary: string): Promise<any> {
    return this.post<any>('/smart-summary/embedding', { summary });
  }

  public getSmartSummaryAccuracy(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    summary: SmartSummary,
    query: string,
    projectId: string,
    similarity: string,
    timezone: string
  ): Promise<AccuracyResults> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/summary-accuracy`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/summary-accuracy`,
        projectId
      );
    }
    const body = {};
    body['summary'] = summary.summary;
    body['similarity'] = similarity;
    body['breakdownField'] = summary.breakdownField ? summary.breakdownField : undefined;
    body['breakdownValue'] = summary.breakdownValue ? summary.breakdownValue : undefined;
    return this.post<AccuracyResults>(url, body, {}, { query, timezone });
  }

  public dumpEmbeddings(
    collectionName: string,
    analysisGroupId: string,
    analysisId: string,
    summary: SmartSummary,
    query: string,
    projectId: string,
    similarity: string,
    timezone: string
  ): Promise<EmbeddingDocumentDump[]> {
    let url: string;
    if (analysisId) {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisId
        )}/dump-embeddings`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${encodeURIComponent(analysisGroupId)}/dump-embeddings`,
        projectId
      );
    }
    const body = {};
    body['summary'] = summary.summary;
    body['similarity'] = similarity;
    body['breakdownField'] = summary.breakdownField ? summary.breakdownField : undefined;
    body['breakdownValue'] = summary.breakdownValue ? summary.breakdownValue : undefined;
    return this.post<EmbeddingDocumentDump[]>(url, body, {}, { query, timezone });
  }

  public generateAnalysisEmbeddings(
    projectId: string,
    collectionName: string,
    analysisId: string,
    query: string,
    timezone: string,
    model: string,
    strategy: string
  ): Promise<string> {
    const url = urlFor(
      `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
        analysisId
      )}/generate-embeddings`,
      projectId
    );
    return this.post<string>(url, {}, {}, { query, timezone, model, strategy });
  }

  public removeAnalysisEmbeddings(
    projectId: string,
    collectionName: string,
    analysisId: string
  ): Promise<void> {
    const url = urlFor(
      `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
        analysisId
      )}/delete-embeddings`,
      projectId
    );
    return this.delete<void>(url);
  }

  public removeSmartSummary(projectId: string, summaryId: string): Promise<void> {
    return this.delete<void>(urlFor(`/smart-summary/${encodeURIComponent(summaryId)}`, projectId));
  }

  public saveSmartSummary(projectId: string, summary): Promise<InteractiveSmartSummary> {
    return this.post<InteractiveSmartSummary>(
      urlFor(`/smart-summary/${encodeURIComponent(summary.id)}`, projectId),
      summary,
      {},
      {}
    );
  }

  public checkAnalysisEmbeddings(
    projectId: string,
    collectionName: string,
    analysisId: string,
    query: string,
    timezone: string,
    model: string,
    strategy: string
  ): Promise<CheckEmbeddingsResult> {
    const url = urlFor(
      `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
        analysisId
      )}/check-embeddings`,
      projectId
    );
    return this.get<CheckEmbeddingsResult>(url, { query, timezone, model, strategy }, {});
  }
}
